import { useCallback, useEffect, useState } from "react";

import { BenchApiError } from "../api/http";
import { type ProjectOption, fetchProjects } from "../api/projects";

export type ProjectsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: ProjectOption[]; detail: string | null };

export interface ProjectsController {
  state: ProjectsState;
  reload: () => Promise<void>;
}

/**
 * Loads the tenant's projects for the scope picker.
 *
 * `tenantKey` changes whenever the deployment, the identity or the tenant does -- each can mean
 * a different answer -- and not when the project or working session is saved, which is the one
 * edit the picker itself causes. A reload keeps the previous list on screen rather than blanking
 * the select underneath the tester.
 */
export function useProjects(tenantKey: string | null): ProjectsController {
  const [state, setState] = useState<ProjectsState>({ kind: "idle" });

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    setState((current) => (current.kind === "ready" ? current : { kind: "loading" }));
    try {
      const response = await fetchProjects(signal);
      if (signal?.aborted) {
        return;
      }
      setState({ kind: "ready", items: response.items, detail: response.detail });
    } catch (error) {
      if (!signal?.aborted) {
        setState({ kind: "error", message: describe(error) });
      }
    }
  }, []);

  useEffect(() => {
    if (tenantKey === null) {
      setState({ kind: "idle" });
      return;
    }
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [tenantKey, load]);

  return { state, reload: () => load() };
}

function describe(error: unknown): string {
  return error instanceof BenchApiError ? error.message : `Unexpected failure: ${String(error)}`;
}
